import {
  Component,
  OnInit,
  ViewChild,
  inject,
  signal,
  computed,
  effect,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { ExportsComponent } from '../exports/exports.component'
import { Checkout, Depot, Tag } from 'depoto-core/src/entities'
import { createSubmittedFilterState, SchemaUtil, withDateBoundary } from '../../utils'
import moment from 'moment'
import { soldItemsColumns } from '../../utils/definitions.util'
import { CheckoutService } from '../../services/entity-services/checkout.service'
import { DepotService } from '../../services/entity-services/depot.service'
import { PaymentService } from '../../services/entity-services/payment.service'
import { ProducerService } from '../../services/entity-services/producer.service'
import { VatService } from '../../services/entity-services/vat.services'
import { SupplierService } from '../../services/entity-services/supplier.services'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'
import { TagService } from '../../services/entity-services/tag.service'
import { SoldItemService } from '../../services/entity-services/soldItem.service'
import { CompanyCarrierService } from '../../services/entity-services/companyCarrier.service'

const DEFAULT_FILTERS = {
  productFulltext: '',
  customerFulltext: '',
  dateCreated: {
    right_date: moment().clone().startOf('month').format('YYYY-MM-DD'),
    left_date: '',
  },
  dateBill: {
    right_date: '',
    left_date: '',
  },
  status: 'all',
  processStatus: 'all',
  carriers: [],
  depots: [],
  checkouts: [],
  payments: [],
  producers: [],
  suppliers: [],
  orderTags: [],
  productTags: [],
  shippingCountries: [],
  product: 0,
}

@Component({
  selector: 'app-sold-items',
  templateUrl: 'sold-items.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SoldItemsComponent extends BaseListing implements OnInit {
  private soldItemService = inject(SoldItemService)
  private checkoutService = inject(CheckoutService)
  private depotService = inject(DepotService)
  private paymentService = inject(PaymentService)
  private producerService = inject(ProducerService)
  private tagService = inject(TagService)
  private vatService = inject(VatService)
  private companyCarrierService = inject(CompanyCarrierService)
  private supplierService = inject(SupplierService)

  @ViewChild('exportsModal') exportsModal: ExportsComponent

  listingColumns = signal(soldItemsColumns)

  sortProp = signal('quantity')
  sortDir = signal('desc')

  filters = signal(structuredClone(DEFAULT_FILTERS))
  filtersExpanded = signal(false)
  readonly defaultFilters = DEFAULT_FILTERS
  readonly advancedFilterKeys = [
    'payments',
    'status',
    'processStatus',
    'carriers',
    'orderTags',
    'productTags',
    'producers',
    'suppliers',
    'dateBill',
  ]

  statusTypesOptions: string[] = ['all', 'bill', 'reservation']
  processStatesOptions: string[] = [
    'all',
    'received',
    'picking',
    'packing',
    'packed',
    'dispatched',
    'delivered',
    'returned',
    'picking_error',
    'cancelled',
  ]

  orderTags: Tag[] = []
  productTags: Tag[] = []

  readonly queryCheckouts = injectQuery(() => ({
    queryKey: ['checkouts', 'options'],
    queryFn: async () => {
      return await this.checkoutService
        .getCheckouts({ page: 1, sort: 'name', direction: 'asc' })
        .then(result => result.items)
    },
    select: data => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.checkouts?.length) {
        // TODO CORE USER
        return []
      } else if (
        !this.checkoutService.doesUserHaveAllCheckouts(data as any[]) &&
        this.core.services.user.user?.checkouts?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.checkouts // TODO CORE USER
      } else {
        return data
      }
      // (data as any[]).filter(tag => tag.type === 'order')
    },
  }))

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        // TODO CORE USER
        return []
      } else {
        return await this.depotService
          .getDepots(
            {
              sort: 'name',
              direction: 'asc',
            },
            { id: null, name: null, enabled: null }
          )
          .then(result => result.items)
      }
    },
    select: data => {
      if (
        !this.depotService.doesUserHaveAllDepots(data as any[]) &&
        this.core.services.user.user?.depots?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.depots // TODO CORE USER
      } else {
        return data as any[]
      }
    },
  }))

  readonly queryPayments = injectQuery(() => ({
    queryKey: ['payments'],
    queryFn: async () =>
      await this.paymentService.getPayments({}, { id: null, name: null, type: { id: null } }).then(r => r.items),
  }))

  readonly queryProducers = injectQuery(() => ({
    queryKey: ['producers'],
    queryFn: async () => await this.producerService.getProducers({}, { id: null, name: null }).then(r => r.items),
  }))

  readonly querySuppliers = injectQuery(() => ({
    queryKey: ['suppliers'],
    queryFn: async () => {
      return await this.supplierService
        .getSuppliers({ sort: 'name', direction: 'asc' }, { id: null, name: null })
        .then(r => r.items)
    },
  }))

  readonly queryTags = injectQuery(() => ({
    queryKey: ['tags', 'options'],
    // todo purcasr or clasic order
    // queryFn: async () => await this.tagService.getTags({ filters: { type: 'purchase_order' }}, { id: null, name: null, type: null }).then(r => r.items),
    queryFn: async () =>
      await this.tagService.getTags({ sort: 'name' }, { id: null, name: null, type: null }).then(r => r.items),
    select: data => (data as any[]).filter(tag => tag.type === 'order'),
  }))

  readonly queryCompanyCarriers = injectQuery(() => ({
    queryKey: ['companyCarriers', 'options'],
    queryFn: async () => {
      return await this.companyCarrierService
        .getCompanyCarriers({}, { id: null, carrier: SchemaUtil.carrier })
        .then(result => {
          return [
            ...new Map(
              (result?.items ?? []).map(companyCarrier => companyCarrier.carrier).map(d => [d.id, d])
            ).values(),
          ]
        })
    },
    select: data => {
      return (data as any)?.sort((a, b) => {
        if (a.name < b.name) return -1
        if (a.name > b.name) return 1
        return 0
      })
    },
  }))

  readonly queryFilters = computed(() => {
    const src = this.filters()
    const out: any = {}

    for (const [key, value] of Object.entries(src)) {
      // Skip blanks and 'all'
      const isEmptyArray = Array.isArray(value) && value.length === 0
      if (value === undefined || value === null || value === '' || value === 'all' || value === 0 || isEmptyArray) {
        continue
      }

      // Date ranges present in Sold Items
      if (key === 'dateCreated' || key === 'dateBill') {
        const left = (value as any)?.left_date
        const right = (value as any)?.right_date
        if ((left && left.length) || (right && right.length)) {
          out[key] = {
            ...(left?.length && {
              left_date: left,
            }),
            ...(right?.length && {
              right_date: withDateBoundary(right, 'end'),
            }),
          }
        }
        continue
      }

      // Arrays of entity objects → array of ids
      if (Array.isArray(value) && (value as any)[0]?.id) {
        out[key] = (value as any).map((o: any) => o?.id)
        continue
      }

      // Single object with id → id
      if ((value as any)?.id) {
        out[key] = (value as any).id
        continue
      }

      // Tri-state strings 'yes'/'no' → boolean (kept generic)
      if (value === 'yes' || value === 'no') {
        out[key] = value === 'yes'
        continue
      }

      // Arrays of primitives (e.g., shippingCountries) or other primitives
      out[key] = value
    }

    // checkout fallback (mirror Orders behavior)
    const checkouts = this.queryCheckouts.data() ?? []
    if (
      !this.checkoutService.doesUserHaveAllCheckouts(checkouts as any) &&
      this.core.services.user.user?.checkouts?.length &&
      !out['checkouts']?.length
    ) {
      out['checkouts'] = (checkouts as Checkout[]).map(ch => ch.id)
    }

    // depots fallback (mirror Orders behavior)
    const depots = this.queryDepotsOptions.data() ?? []
    if (
      !this.depotService.doesUserHaveAllDepots(depots as any) &&
      this.core.services.user.user?.depots?.length && // TODO CORE USER
      !out['depots']?.length
    ) {
      out['depots'] = (depots as Depot[]).map(d => d.id)
    }

    // cleanup undefined/empty after fallbacks
    Object.keys(out).forEach(k => {
      const val: any = out[k]
      if (val === undefined || val === null || val === '' || (Array.isArray(val) && !val.length)) {
        delete out[k]
      }
    })

    return out
  })

  private readonly submittedFilters = createSubmittedFilterState<typeof DEFAULT_FILTERS, Record<string, any>>({
    saveView: filters => this.saveSubmittedView(filters),
    refetch: () => this.querySoldItems.refetch(),
  })
  readonly submittedQueryFilters = computed(() => this.submittedFilters.snapshot()?.request ?? {})
  readonly submittedViewFilters = computed(() => this.submittedFilters.snapshot()?.view ?? this.filters())

  stringifiedFilters = computed(() => {
    const copy = structuredClone(this.submittedViewFilters())
    copy.productFulltext = ''
    copy.producers = []
    copy.suppliers = []
    return JSON.stringify(copy)
  })

  readonly querySoldItems = injectInfiniteQuery(() => {
    const snapshot = this.submittedFilters.snapshot()
    const sortProp = this.sortProp()
    const sortDir = this.sortDir()
    const requestFilters = snapshot?.request ?? {}

    return {
      enabled: this.submittedFilters.ready(),
      queryKey: ['soldItems', 'list', { sortProp, sortDir, filters: requestFilters }],
      queryFn: ({ pageParam }) =>
        this.soldItemService.getSoldItems(
          {
            page: pageParam,
            sort: sortProp,
            direction: sortDir,
            filters: requestFilters,
          },
          SchemaUtil.soldItem
        ),
      initialPageParam: 1,
      getNextPageParam: result =>
        Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
          ? Number(result?.paginator?.current) + 1
          : undefined,
    }
  })

  totalItems = computed(() => this.querySoldItems.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  areCoreDataLoading = computed(() => {
    return (
      this.queryCheckouts.isFetching() ||
      this.queryDepotsOptions.isFetching() ||
      this.queryPayments.isFetching() ||
      this.queryTags.isFetching() ||
      this.queryProducers.isFetching() ||
      this.queryCompanyCarriers.isFetching() ||
      this.querySuppliers.isFetching()
    )
  })

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryDepotsOptions.data()
      this.querySuppliers.data()
      this.queryCompanyCarriers.data()
      this.queryCheckouts.data()
      this.queryTags.data()
      this.queryPayments.data()
      this.queryProducers.data()
      this.querySoldItems.data()
    })
    effect(() => {
      if (!this.submittedFilters.ready()) return
      const sortProp = this.sortProp()
      const sortDir = this.sortDir()
      untracked(() => this.saveSubmittedView(undefined, sortProp, sortDir))
    })
  }

  async ngOnInit() {
    const view = this.loadViewSettings('soldItems')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'quantity')
    this.sortDir.set(view?.sortDir || 'desc')
    this.filtersExpanded.set(false)
    await this.refreshFiltersData()
    this.submittedFilters.commit(this.filters(), this.queryFilters())
    this.querySoldItems.refetch()
  }

  async refreshFiltersData(): Promise<void> {
    const allTags = this.queryTags.data() ?? []
    this.orderTags = allTags.filter(t => t.type === 'order')
    this.productTags = allTags.filter(t => t.type === 'product')
  }

  clearFilters() {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.onFilterSubmit()
  }

  onFilterSubmit(): void {
    this.submittedFilters.submit(this.filters(), this.queryFilters())
  }

  private saveSubmittedView(
    filters = this.submittedFilters.snapshot()?.view ?? structuredClone(DEFAULT_FILTERS),
    sortProp = this.sortProp(),
    sortDir = this.sortDir()
  ): void {
    this.saveViewSettings('soldItems', { filters, sortProp, sortDir })
  }

  setFilters(property: string, value: any, dateLeftRight?: string) {
    if (dateLeftRight) {
      this.filters.set({
        ...this.filters(),
        [property]: {
          ...this.filters()[property],
          [dateLeftRight]: value,
        },
      })
    } else {
      this.filters.set({
        ...this.filters(),
        [property]: value,
      })
    }
  }

  protected readonly DEFAULT_FILTERS = DEFAULT_FILTERS
}
